import { AfterViewInit, ChangeDetectionStrategy, Component, effect, Input, signal, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { UserOverview } from '@core/core.state';
import { getDateAsNumber } from '@core/customer-space-admin/utils/cs-user.utils';
import { MatSort } from '@angular/material/sort';
import { UserType } from '@platform/components/user-is-external-dropdown/user-is-external-dropdown.component';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { IOptionsStoreWithFilter, OptionsStoreService, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';

interface UserManagementTableFilter {
  userType: UserType;
}

@Component({
  selector: 'app-user-management-table',
  templateUrl: './user-management-table.component.html',
  styleUrls: ['./user-management-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserManagementTableComponent implements AfterViewInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  public readonly userType = signal<UserType>(null);
  public readonly search = signal('');

  public readonly dataSource: MatTableDataSource<UserOverview>;

  public readonly displayedColumns: string[] = [
    'firstName',
    'lastName',
    'account',
    'organization',
    'email',
    'createdDate',
    'lastLogin',
    'isActive',
    'actions',
  ];

  private readonly allUsers = signal<UserOverview[]>([]);

  private readonly optionsStore: IOptionsStoreWithFilter<UserManagementTableFilter>;

  constructor(
    private translateService: TranslateService,
    optionsStoreService: OptionsStoreService,
  ) {
    this.dataSource = new MatTableDataSource<UserOverview>();
    this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
    this.dataSource.filterPredicate = this.filterPredicate;

    this.optionsStore = optionsStoreService.getOptionsStore<UserManagementTableFilter>(UiTableKeys.PortalUsers, { userType: null });
    this.optionsStore.registerFilterSignal('userType', this.userType);

    effect(() => {
      const userType = this.userType();
      this.dataSource.data = this.allUsers().filter((u) => userType === null || (userType === 'internal') === u.isInternal);
    });
    effect(() => (this.dataSource.filter = this.search()));
  }

  @Input({ required: true }) set users(value: UserOverview[]) {
    this.allUsers.set(value);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.optionsStore.registerSort(this.sort);
    this.optionsStore.registerPaginatorPageSize(this.paginator);
  }

  private sortingDataAccessor = (item: UserOverview, property: string) => {
    switch (property) {
      case 'firstName':
        return item.firstName;
      case 'lastName':
        return item.lastName;
      case 'account':
        return item.isInternal ? 'internal' : 'external';
      case 'organization':
        return item.organizationUnit;
      case 'email':
        return item.mail;
      case 'createdDate':
        return getDateAsNumber(item.accountCreatedDate);
      case 'lastLogin':
        return getDateAsNumber(item.lastSuccessfulLoginDate);
      case 'isActive':
        return item.isActive ? 'active' : 'inactive';
      default:
        return item[property as keyof UserOverview]?.toString() ?? '';
    }
  };

  private filterPredicate = (item: UserOverview, filter: string) => {
    const activeT = this.translateService.instant('users.active');
    const inactiveT = this.translateService.instant('users.inactive');

    if (filter.toLowerCase() === activeT.toLowerCase()) {
      return item.isActive;
    }
    if (filter.toLowerCase() === inactiveT.toLowerCase()) {
      return !item.isActive;
    }

    const concatFields = concatUserFields(item, this.translateService);
    return concatFields.includes(filter.toLowerCase());
  };
}

function concatUserFields(data: UserOverview, translateService: TranslateService): string {
  const activeT = translateService.instant('users.active');
  const inactiveT = translateService.instant('users.inactive');
  const arr: string[] = [
    data.firstName,
    data.lastName,
    data.organizationUnit,
    data.mail,
    data.mobileNumber,
    data.isActive ? activeT : inactiveT,
    data.isInternal ? 'Internal' : 'External',
    data.id,
  ];

  return arr
    .filter((item) => item != null)
    .map((item) => item.toLowerCase())
    .join(',');
}
